import React, {useState} from 'react';
import {connect} from 'react-redux';
import Placeholder from '../../Placeholder';
import SchoolContactInfo from '../sidebar/SchoolContactInfo';
import PaymentProfileSummary from '../sidebar/PaymentProfileSummary';
import SideBar from '../sidebar/SideBar';
import BillingAccountOverview from './BillingAccountOverview';

import {default as duckify, dependentDuck, singleDuck, filterDuck} from "../../../utils/duckify";
import { customers, users, billingAccounts, modals } from '../../../ducks/index';
import { renderedSnippet } from '../../../ducks/webSnippets';

import './styles/Billing.scss';
import FontAwesome from 'utils/FA';
import BulkActions from 'components/ContactList/BulkActions/BulkActions';
import BulkAction from 'components/ContactList/BulkActions/BulkAction';
import moment from 'moment';
import {faFileAlt} from '@fortawesome/pro-solid-svg-icons/faFileAlt';
import withFiltering from '../../../hocs/withFiltering';
import { compose } from 'redux';
import { faCreditCard } from '@fortawesome/pro-light-svg-icons/faCreditCard';
import BillingAccountAccessList from '../sidebar/BillingAccountAccessList';
import MonthlyStatementGenerator from '../../billing/MonthlyStatementGenerator';
import ManageAutopayDates from '../sidebar/ManageAutopayDates';
import BillingItemList from '../../billing/BillingItemList';
import LinkButton from '../../LinkButton/LinkButton';
import AutopayInstallmentList from '../../billing/AutopayInstallmentList';
import { formatCurrency } from '../../../utils';
import { paymentTypes } from '../../../components/payments/paymentInputs/PaymentSelector'

const Billing = ({allDucksReady, billingAccounts, billingSnippet, customer, filters, setFilters}) => (
  <Placeholder.loading ready={allDucksReady} content={() => <React.Fragment>
    <BillingWidget billingSnippet={billingSnippet} />

    <div className={'billing-account-section'}>
      { billingAccounts.length === 0 && <div>You do not have any billing accounts yet.</div> }
      { billingAccounts.length > 0 && <BillingAccounts billingAccounts={billingAccounts} customer={customer} filters={filters} setFilters={setFilters}/>}
    </div>
  </React.Fragment>}/>
);

const BillingWidget = ({ billingSnippet }) => {
  if (!billingSnippet.body) {
    return null;
  }

  return (
    <div className='billing-widget' dangerouslySetInnerHTML={{__html: billingSnippet.body}} />
  );
};

const BillingAccounts = ({billingAccounts, customer, filters, setFilters}) => {
  const [showHistory, setShowHistory] = useState(false);
  const selectedAccountId = filters && filters.billing_account_id || billingAccounts[0].id;
  const billingAccount = billingAccounts.find(account => account.id === selectedAccountId);
  const paymentMethods = customer.payment_config.payment_methods
  const depositMethods = customer.deposit_payment_methods
  const convenienceFees = Object.keys(paymentMethods).filter(key => paymentMethods[key].canonical_name === paymentTypes.CREDIT_CARD || paymentMethods[key].canonical_name === paymentTypes.ACH)
    .map(payment => (
      {
        canonicalName: paymentMethods[payment].canonical_name,
        displayName: paymentMethods[payment].display_name,
        percent: paymentMethods[payment].convenience_fee.percent ? paymentMethods[payment].convenience_fee.percent : 0,
        flatAmount: paymentMethods[payment].convenience_fee.fee_amount ? paymentMethods[payment].convenience_fee.fee_amount : 0,
        description: paymentMethods[payment].convenience_fee.charge_description,
        scenarios: paymentMethods[payment].convenience_fee.scenarios,
      }
    ))

  const convenienceFeeDisplay = convenienceFees.map(fee => {
    let feeTypes = ''
    if ((fee.scenarios.includes('billing') && depositMethods.includes(fee.canonicalName)) && fee.flatAmount != 0 && fee.percent != 0) {
      feeTypes = `${fee.percent}% plus ${formatCurrency(fee.flatAmount)}`
    }
    else if ((fee.scenarios.includes('billing') && depositMethods.includes(fee.canonicalName)) && fee.flatAmount != 0 && fee.percent == 0){
      feeTypes = `${formatCurrency(fee.flatAmount)}`
    }
    else if ((fee.scenarios.includes('billing') && depositMethods.includes(fee.canonicalName)) && fee.flatAmount == 0 && fee.percent != 0) {
      feeTypes = `${fee.percent}%`
    }

    // Only want to display if a convenience fee has been set or if billing is not enabled.
    if (feeTypes === '') {
      return ''
    }
    else {
      return (
        <p>
          <b>Please note:</b> a {fee.description} of {feeTypes} will be applied to all {fee.displayName} payments.
        </p>
      )
    }
  }
  )

  return <div className={'billing-account'}>
    {convenienceFeeDisplay}
    <h2>
      <FontAwesome icon={faCreditCard} nbsp/>
      {billingAccount.contact.name}
      {billingAccounts.length > 1 &&
      <BillingAccountSelector billingAccounts={billingAccounts} selectedAccountId={selectedAccountId}
                              setFilters={setFilters}/>}
    </h2>
    <BillingAccountOverview billingAccount={billingAccount} showMakePayment/>
    <div className="billing-history">
      <div className='billing-history-header'>
        <div>
          <h2 className="no-style">
            <FontAwesome name={'list'} nbsp/>
            Ledger
          </h2>
        </div>

        <div>
          <h2 className="no-style">
            {customer.locale !== 'en-CA' && <TaxStatementViewer billingAccount={billingAccount}/>}

            <a className="billing-history-statement-link" href={`/billing_accounts/${billingAccount.id}/statement`}
              target='_blank'>
              <FontAwesome name='file-text-o' /> Summary Statement
            </a>

            <div className='statement-year-selector'>
              <MonthlyStatementGenerator billingAccountId={billingAccount.id} />
            </div>
          </h2>
        </div>
      </div>
      <LinkButton content={showHistory ? 'Show Installments' : 'Show History'} onClick={() => setShowHistory(!showHistory)} />

      {billingAccount.id && (showHistory
        ? <BillingItemList perPage={20} billingAccountId={billingAccount.id} adminView={false} excludePrepayment />
        : <AutopayInstallmentList perPage={20} billingAccountId={billingAccount.id} />)}
    </div>
  </div>
};

const BillingAccountSelector = ({ billingAccounts, setFilters }) => {
  return (
    <div className={'billing-account-selector'}>
      <BulkActions>
        <BulkAction icon={faFileAlt} title={'Select Account'}>
          {billingAccounts.map((account) => (
            <BulkAction title={account.contact.name}
                        icon={faCreditCard}
                        onClick={() => setFilters({billing_account_id: account.id})}
                        key={account.id}
            />
          ))}
        </BulkAction>
      </BulkActions>
    </div>
  );
}

const getTaxStatement = (billingAccount, year) => {
  const url = `/billing_accounts/${billingAccount.id}/tax_statement?year=${year}`;
  return (
    modals.actions.show(
      'Downloader', {
        async: false,
        url,
        autoClose: true,
        httpMethod: 'GET'
      }
    ));
};

const TaxStatementViewer = connect()(({ billingAccount, dispatch }) => {
  // List three most recent years
  const currentYear = moment().year();
  const years = [currentYear, currentYear - 1, currentYear - 2];
  return (
    <div className='statement-year-selector'>
      <BulkActions>
        <BulkAction icon={faFileAlt} title='Tax Statements'>
          {years.map((year) => (
            <BulkAction title={year} onClick={() => dispatch(getTaxStatement(billingAccount, year))} />
          ))}
        </BulkAction>
      </BulkActions>
    </div>
  );
});

function mapDucksToProps(){
  const user = singleDuck(users, 'current');

  return {
    billingSnippet: renderedSnippet('billing_management'),
    customer: singleDuck(customers, 'current'),
    user,
    billingAccounts: dependentDuck(user, ({value : currentUser}) =>
      filterDuck(billingAccounts, {user_id: currentUser.id})
    )
  };
}

export default {
  body:
    compose(
      withFiltering,
      duckify(mapDucksToProps)
    )(Billing),
  sideBar: () => <SideBar>
    <PaymentProfileSummary/>
    <ManageAutopayDates />
    <BillingAccountAccessList/>
    <SchoolContactInfo/>
  </SideBar>
};
